import React, { Component } from "react";
import {
  IonPage,
  IonContent,
  IonRow,
  IonCard,
  IonInput,
  IonButton,
  IonLabel,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonBackButton,
  IonTitle,
  IonLoading,
  isPlatform,
  IonModal,
} from "@ionic/react";

import { OAuth2AuthenticateOptions, OAuth2Client } from "@byteowls/capacitor-oauth2";

import "./LoginPage.css";
import "../../Shared.css";
import "../../../App.css";

import { IScreenProps, IUser } from "../../Interfaces";
import AppController from "../../../controllers/AppController";
import AuthenticationController from "../../controllers/AuthenticationController";

import AppHeader from "../../components/AppHeader";
import AppCard from "../../components/AppCard";
import Dropdown from "../../components/Dropdown";

import iconError from "../../assets/Button_Alert@3x.png";
import iconHidePassword from "../../assets/Button_Display_Hide@3x.png";
import iconShowPassword from "../../assets/Button_Display_Show@3x.png";
import buttonClose from "../../assets/Button_Close@3x.png";
import iconClose from "../../assets/Button_Close@3x.png";
import disclosureRight from "../../assets/Button_Disclosure_Right_Grey50@3x.png";

import DataController from "../../controllers/DataController";
import { Preferences } from "@capacitor/preferences";

type Props = { addingAccount: boolean; screenProps: IScreenProps; features: string[] };
type State = {
  emailText: string;
  emailConfirmationText: string;
  passwordText: string;
  passwordConfirmationText: string;
  firstNameText: string;
  lastNameText: string;
  emailActive: boolean;
  emailConfirmationActive: boolean;
  passwordActive: boolean;
  passwordConfirmationActive: boolean;
  firstNameActive: boolean;
  lastNameActive: boolean;
  tempPasswordText: string;
  tempPasswordActive: boolean;
  loginError: boolean;
  showTemporaryLoginErrorMessage: boolean;
  showTemporaryLogin: boolean;
  trustDropdownValue: string;
  showTemporaryLoginIncorrectPassword: boolean;
  passwordHidden: boolean;
  errorMessage: string;
  submitButtonEnabled: boolean;
  loginType: "login" | "register";
  isLoggingIn: boolean;
  showWebBanner: boolean;
  showOlark: boolean;
  ssoTrusts: string[];
  showSSOModal: boolean;
};

class LoginPage extends Component<Props, State> {
  private passwordField: React.RefObject<HTMLIonInputElement>;
  private passwordConfirmationField: React.RefObject<HTMLIonInputElement>;

  constructor(props: Props) {
    super(props);

    this.passwordField = React.createRef();
    this.passwordConfirmationField = React.createRef();

    this.state = {
      emailText: "",
      emailConfirmationText: "",
      passwordText: "",
      passwordConfirmationText: "",
      firstNameText: "",
      lastNameText: "",
      emailActive: false,
      emailConfirmationActive: false,
      passwordActive: false,
      passwordConfirmationActive: false,
      firstNameActive: false,
      lastNameActive: false,
      tempPasswordText: "",
      tempPasswordActive: false,
      loginError: false,
      showTemporaryLoginErrorMessage: false,
      showTemporaryLogin: false,
      trustDropdownValue: "Select your Trust",
      showTemporaryLoginIncorrectPassword: false,
      passwordHidden: true,
      errorMessage: "",
      submitButtonEnabled: false,
      loginType: "login",
      isLoggingIn: false,
      showWebBanner: false,
      showOlark: false,
      ssoTrusts: [],
      showSSOModal: false,
    };
  }

  async componentDidMount() {
    this.updateLoginPageSettings();
    if (this.props.screenProps.hostApp !== "iCPG") {
      let interval = setInterval(() => {
        this.updateLoginPageSettings();
        if (this.state.ssoTrusts.length > 0) {
          clearInterval(interval);
        }
      }, 3000);
    }
  }

  async componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {}

  async updateLoginPageSettings() {
    try {
      let showOlark = await DataController.getShouldShowOlark();
      let ssoTrusts = await DataController.getSSOTrusts();
      this.setState({ showOlark, ssoTrusts });
    } catch (error) {
      console.log(error);
    }
  }

  emailChanged(event: any) {
    const value = event.target.value.trim();

    this.updatePasswordField();

    this.setState({ emailText: value }, () => {
      this.validateEntry();
    });
  }

  emailConfirmationChanged(event: any) {
    const value = event.target.value.trim();

    this.updatePasswordField();

    this.setState({ emailConfirmationText: value }, () => {
      this.validateEntry();
    });
  }

  updatePasswordField() {
    try {
      setTimeout(() => {
        // workaround setting password text here as password field doesn't autofill correctly on iOS
        let passwordInput = document.getElementById("passwordInput")?.children[0] as HTMLIonInputElement;
        let passwordText = this.state.passwordText;
        if (passwordInput != null && typeof passwordInput.value === "string") {
          passwordText = passwordInput.value;
        }

        this.setState({ passwordText: passwordText }, () => {
          this.validateEntry();
        });
      }, 1000);
    } catch (error) {}
  }

  passwordChanged(event: any) {
    const value = event.target.value;

    this.setState({ passwordText: value }, () => {
      this.validateEntry();
    });
  }

  passwordConfirmationChanged(event: any) {
    const value = event.target.value;

    this.setState({ passwordConfirmationText: value }, () => {
      this.validateEntry();
    });
  }

  firstNameChanged(event: any) {
    const value = event.target.value;

    this.setState({ firstNameText: value }, () => {
      this.validateEntry();
    });
  }

  lastNameChanged(event: any) {
    const value = event.target.value;

    this.setState({ lastNameText: value }, () => {
      this.validateEntry();
    });
  }

  tempPasswordChanged(event: any) {
    const value = event.target.value;

    this.setState({ tempPasswordText: value }, () => {
      this.validateEntry();
    });
  }

  validateEntry() {
    var inputIsOK = true;

    var validEmail = this.validateEmail(this.state.emailText);

    if (validEmail === false) {
      inputIsOK = false;
    }

    if (this.state.passwordText.length < 1) {
      inputIsOK = false;
    }

    if (this.state.loginType === "register") {
      if (this.state.firstNameText.length < 1) {
        inputIsOK = false;
      }

      if (this.state.lastNameText.length < 1) {
        inputIsOK = false;
      }

      if (this.state.emailConfirmationText.length < 1) {
        inputIsOK = false;
      }

      if (this.state.passwordConfirmationText.length < 1) {
        inputIsOK = false;
      }
    }

    this.setState({ submitButtonEnabled: inputIsOK });
  }

  validateEmail(email: string) {
    var tester =
      /^[-!#$%&'*+\/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-?\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

    if (!email) return false;

    if (email.length > 254) return false;

    var valid = tester.test(email);
    if (!valid) return false;

    // Further checking of some things regex can't handle
    var parts = email.split("@");
    if (parts[0].length > 64) return false;

    var domainParts = parts[1].split(".");
    if (
      domainParts.some(function (part) {
        return part.length > 63;
      })
    )
      return false;

    return true;
  }

  enterButtonClicked(event: any) {
    if (event.key === "Enter") {
      if (this.state.emailText !== "" && this.state.passwordText !== "") {
        this.submit();
      }
    }
  }

  validateBeforeSubmission() {
    try {
      if (this.state.emailText !== this.state.emailConfirmationText) {
        throw `Email confirmation text doesn't match`;
      }

      if (this.state.passwordText !== this.state.passwordConfirmationText) {
        throw `Password confirmation text doesn't match`;
      }

      if (this.state.emailText.length > 0) {
        var emailParts = this.state.emailText.trim().split("@");
        var name = emailParts.length > 1 ? emailParts[0] : null;

        let invalidNames = [
          "abuse",
          "admin",
          "email",
          "feedback",
          "hostmaster",
          "junk",
          "mail",
          "mailerdaemon",
          "marketing",
          "news",
          "newsletter",
          "nobody",
          "none",
          "noreply",
          "nospam",
          "null",
          "ops",
          "orders",
          "postmaster",
          "press",
          "remove",
          "root",
          "security",
          "spam",
          "subscribe",
          "unsubscribe",
          "www",
        ];

        if (name != null && invalidNames.includes(name)) {
          throw `Email address name isn't allowed. Please try a different email.`;
        }
      }

      return true;
    } catch (error) {
      if (typeof error === "string") {
        this.setState({ errorMessage: error, loginError: true });
      }

      return false;
    }
  }

  async submit() {
    try {
      this.setState({ isLoggingIn: true });

      if (this.state.loginType === "login") {
        let user = await AuthenticationController.logIn(
          this.state.emailText,
          this.state.passwordText,
          this.props.screenProps.hostApp
        );

        this.props.screenProps.userUpdated(user, true);
      } else {
        let isValid = this.validateBeforeSubmission();

        if (isValid) {
          let success = await AuthenticationController.registerUser(
            this.state.emailText,
            this.state.passwordText,
            this.state.firstNameText,
            this.state.lastNameText,
            this.props.screenProps.hostApp
          );

          if (success) {
            this.switchLoginType();
            setTimeout(() => {
              this.submit();
            }, 500);
          }
        }
      }
    } catch (error: any) {
      console.log(error);

      if (error != null) {
        if (typeof error === "string") {
          this.setState({ errorMessage: error, loginError: true });
        } else if (typeof error === "object") {
          if (error.errorMessage != null) {
            if (error.serverError === true) {
              this.setState({ showTemporaryLoginErrorMessage: true });
            }

            if (
              this.props.screenProps.networkStatus == null ||
              this.props.screenProps.networkStatus.connected === false
            ) {
              this.setState({
                errorMessage: "No network connection. Please connect to the internet and try again.",
                loginError: true,
              });
            } else {
              if (typeof error.errorMessage === "string") {
                this.setState({ errorMessage: error.errorMessage, loginError: true });
              } else if (typeof error.errorMessage === "object") {
                if (error.errorMessage.message != null) {
                  this.setState({ errorMessage: error.errorMessage.message, loginError: true });
                } else {
                  this.setState({ errorMessage: "Error signing in", loginError: true });
                }
              }
            }
          } else {
            this.setState({ errorMessage: "Error signing in", loginError: true });
          }
        } else {
          this.setState({ errorMessage: "Error signing in", loginError: true });
        }
      } else {
        this.setState({ errorMessage: "Error signing in", loginError: true });
      }
    }

    this.setState({ isLoggingIn: false });
  }

  showTemporaryLogin(shouldShow: boolean) {
    this.setState({ showTemporaryLogin: shouldShow });
  }

  async loginTemporarily() {
    if (this.props.screenProps.hostApp === "iCPG" || this.state.trustDropdownValue === "General subscriber") {
      let user = await AuthenticationController.logInTemporarily("");
      this.props.screenProps.userUpdated(user, false);
    } else {
      if (this.props.screenProps.hostApp === "CFR") {
        if (this.state.tempPasswordText !== "999") {
          this.setState({ showTemporaryLoginIncorrectPassword: true });
          return;
        } else {
          this.setState({ showTemporaryLoginIncorrectPassword: false });
        }
      }

      let validTrust = DataController.getTrusts(this.props.screenProps.hostApp).includes(this.state.trustDropdownValue);
      if (validTrust) {
        let user = await AuthenticationController.logInTemporarily(this.state.trustDropdownValue);
        this.props.screenProps.userUpdated(user, false);
      }
    }
  }

  switchLoginType() {
    if (this.state.loginType === "login") {
      this.setState({ loginType: "register" });
    } else {
      this.setState({ loginType: "login" });
    }
    this.resetErrorMessages();
  }

  async loginSSO(trust: string) {
    try {
      let oauthConfig = await AuthenticationController.downloadSSOConfiguration(trust, this.props.screenProps.hostApp);

      // NWAS
      // oauthConfig = {
      //   appId: "41a72370-8cfa-462e-b0af-5ba256923634",
      //   authorizationBaseUrl: `https://login.microsoftonline.com/f65c6dd6-6719-46ed-b3dd-3f6d27f951dd/oauth2/v2.0/authorize`,
      //   scope: "api://41a72370-8cfa-462e-b0af-5ba256923634/Read.JRCALC.User",
      //   accessTokenEndpoint: `https://login.microsoftonline.com/f65c6dd6-6719-46ed-b3dd-3f6d27f951dd/oauth2/v2.0/token`,
      //   responseType: "code",
      //   pkceEnabled: true,
      //   logsEnabled: true,
      //   additionalParameters: {
      //     prompt: "select_account",
      //   },
      //   web: {
      //     // redirectUrl: "https://jrcalcplusweb.co.uk",
      //     redirectUrl: "http://localhost:8100",
      //     // redirectUrl: "https://jrcalcplusweb-nwas-test.netlify.app",
      //     logoutUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/logout",
      //   },
      //   android: {
      //     redirectUrl: "msauth://uk.co.classprofessional.cpg.ukascpg.jrcalcplus/0WQDlLyjvUVRqznH%2Fx2b0Yt2Teo%3D", // See Azure Portal -> Authentication -> Android Configuration "Redirect URI"
      //   },
      //   ios: {
      //     pkceEnabled: true, // workaround for bug #111
      //     redirectUrl: "msauth.uk.co.classprofessional.cpg.ukascpg.jrcalcplus://auth",
      //   },
      // };

      // SCAS
      // oauthConfig = {
      //   appId: "31f4cde3-1fe6-4758-93b4-89694a7eccf6",
      //   authorizationBaseUrl: `https://login.microsoftonline.com/a047cd80-b710-42ff-bf7b-c9935c324160/oauth2/v2.0/authorize`,
      //   // scope: "email openid",
      //   scope: "api://uk.co.classprofessional.cpg.ukascpg.jrcalcplus/Read.JRCALC.User",
      //   accessTokenEndpoint: `https://login.microsoftonline.com/a047cd80-b710-42ff-bf7b-c9935c324160/oauth2/v2.0/token`,
      //   responseType: "code",
      //   pkceEnabled: true,
      //   logsEnabled: true,
      //   additionalParameters: {
      //     prompt: "select_account",
      //   },
      //   web: {
      //     // redirectUrl: "https://jrcalcplusweb.co.uk",
      //     // redirectUrl: "http://localhost:8100",
      //     redirectUrl: "https://jrcalcplusweb-scas-test.netlify.app",
      //     // logoutUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/logout",
      //   },
      //   android: {
      //     redirectUrl: "msauth://uk.co.classprofessional.cpg.ukascpg.jrcalcplus/0WQDlLyjvUVRqznH%2Fx2b0Yt2Teo%3D", // See Azure Portal -> Authentication -> Android Configuration "Redirect URI"
      //   },
      //   ios: {
      //     pkceEnabled: true, // workaround for bug #111
      //     redirectUrl: "msauth.uk.co.classprofessional.cpg.ukascpg.jrcalcplus://auth",
      //   },
      // };

      // SJA
      // oauthConfig = {
      //   appId: "5fa46409-bb98-4ef8-bcb3-6f8455092b7a",
      //   authorizationBaseUrl: `https://login.microsoftonline.com/91d037fb-4714-4fe8-b084-68c083b8193f/oauth2/v2.0/authorize`,
      //   // scope: "email openid",
      //   scope: "api://ee7acff5-c4cc-4e70-a71b-ea6aa462db25/Read.JRCALC.User",
      //   accessTokenEndpoint: `https://login.microsoftonline.com/91d037fb-4714-4fe8-b084-68c083b8193f/oauth2/v2.0/token`,
      //   responseType: "code",
      //   pkceEnabled: true,
      //   logsEnabled: true,
      //   additionalParameters: {
      //     prompt: "select_account",
      //   },
      //   web: {
      //     // redirectUrl: "https://jrcalcplusweb.co.uk",
      //     redirectUrl: "http://localhost:8100",
      //     // redirectUrl: "https://jrcalcplusweb-sja-test.netlify.app",
      //     // logoutUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/logout",
      //   },
      //   android: {
      //     redirectUrl: "msauth://uk.co.classprofessional.cpg.ukascpg.jrcalcplus/0WQDlLyjvUVRqznH%2Fx2b0Yt2Teo%3D", // See Azure Portal -> Authentication -> Android Configuration "Redirect URI"
      //   },
      //   ios: {
      //     pkceEnabled: true, // workaround for bug #111
      //     redirectUrl: "msauth.uk.co.classprofessional.cpg.ukascpg.jrcalcplus://auth",
      //   },
      // };

      // LAS
      // oauthConfig = {
      //   appId: "fb53e214-fc02-4840-8310-4c000ecc4d68",
      //   authorizationBaseUrl: `https://login.microsoftonline.com/37c354b2-85b0-47f5-b222-07b48d774ee3/oauth2/v2.0/authorize`,
      //   // scope: "email openid",
      //   scope: "api://cbc5ed29-0c5c-481f-8e3f-0a736953d3c1/Read.JRCALC.User",
      //   accessTokenEndpoint: `https://login.microsoftonline.com/37c354b2-85b0-47f5-b222-07b48d774ee3/oauth2/v2.0/token`,
      //   responseType: "code",
      //   pkceEnabled: true,
      //   logsEnabled: true,
      //   additionalParameters: {
      //     prompt: "select_account",
      //   },
      //   web: {
      //     // redirectUrl: "https://jrcalcplusweb.co.uk",
      //     redirectUrl: "http://localhost:8100",
      //     // redirectUrl: "https://jrcalcplusweb-sja-test.netlify.app",
      //     // logoutUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/logout",
      //   },
      //   android: {
      //     redirectUrl: "msauth://uk.co.classprofessional.cpg.ukascpg.jrcalcplus/0WQDlLyjvUVRqznH%2Fx2b0Yt2Teo%3D", // See Azure Portal -> Authentication -> Android Configuration "Redirect URI"
      //   },
      //   ios: {
      //     pkceEnabled: true, // workaround for bug #111
      //     redirectUrl: "msauth.uk.co.classprofessional.cpg.ukascpg.jrcalcplus://auth",
      //   },
      // };

      // SECAMB
      // oauthConfig = {
      //   appId: "6c55e429-70ec-480f-9c4a-dea0b1217e1a",
      //   authorizationBaseUrl: `https://login.microsoftonline.com/72eae051-e9ae-4913-8520-9cf261f06118/oauth2/v2.0/authorize`,
      //   // scope: "email openid",
      //   scope: "api://af3ac909-a826-4fe8-81b4-687c709a0239/Read.JRCALC.User",
      //   accessTokenEndpoint: `https://login.microsoftonline.com/72eae051-e9ae-4913-8520-9cf261f06118/oauth2/v2.0/token`,
      //   responseType: "code",
      //   pkceEnabled: true,
      //   logsEnabled: true,
      //   additionalParameters: {
      //     prompt: "select_account",
      //   },
      //   web: {
      //     // redirectUrl: "https://jrcalcplusweb.co.uk",
      //     redirectUrl: "http://localhost:8100",
      //     // redirectUrl: "https://jrcalcplusweb-sja-test.netlify.app",
      //     // logoutUrl: "https://login.microsoftonline.com/common/oauth2/v2.0/logout",
      //   },
      //   android: {
      //     redirectUrl: "msauth://uk.co.classprofessional.cpg.ukascpg.jrcalcplus/0WQDlLyjvUVRqznH%2Fx2b0Yt2Teo%3D", // See Azure Portal -> Authentication -> Android Configuration "Redirect URI"
      //   },
      //   ios: {
      //     pkceEnabled: true, // workaround for bug #111
      //     redirectUrl: "msauth.uk.co.classprofessional.cpg.ukascpg.jrcalcplus://auth",
      //   },
      // };

      if (isPlatform("desktop")) {
        oauthConfig.web.redirectUrl = window.location.origin;
      }

      let response = await OAuth2Client.authenticate(oauthConfig);

      let accessToken = response.access_token;

      if (accessToken == null && response.access_token_response != null) {
        accessToken = response.access_token_response.access_token;
      }

      this.setState({ showSSOModal: false });

      let user = await AuthenticationController.logInSSO(accessToken, trust, this.props.screenProps.hostApp);

      this.props.screenProps.userUpdated(user, true);
    } catch (error: any) {
      console.log(error);

      if (typeof error.errorMessage === "string") {
        this.setState({ errorMessage: error.errorMessage, loginError: true });
      } else if (typeof error.errorMessage === "object") {
        if (error.errorMessage.message != null) {
          this.setState({ errorMessage: error.errorMessage.message, loginError: true });
        } else {
          this.setState({ errorMessage: "Error signing in", loginError: true });
        }
      } else if (typeof error.message === "string") {
        if (error.message === "USER_CANCELLED") {
          // ignore
        } else {
          this.setState({ errorMessage: error.message, loginError: true });
        }
      }
    }

    // try {
    //   let loginResponse = await this.context.instance.loginPopup({
    //     scopes: ["User.Read"] // optional Array<string>
    //   });
    //   let test = '';

    // } catch (error) {
    //   console.log(error);
    // }
  }

  forgotClicked() {
    window.open("https://www.classprofessional.co.uk/app-reset/", "_blank", "noreferrer");
  }

  resetErrorMessages() {
    this.setState({ loginError: false, errorMessage: "", showTemporaryLoginErrorMessage: false });
  }

  dismissWebBanner() {
    this.setState({ showWebBanner: false });
    DataController.saveWebBannerDismissed();
  }

  render() {
    let trusts = DataController.getTrusts(this.props.screenProps.hostApp);
    if (this.props.screenProps.hostApp === "CFR") {
      trusts = ["ACF", "EEAST", "EMAS", "NWAS", "SECAMB", "SJA", "SWAST", "WMAS"];
    }

    let trustDropdownOptions = [{ label: "Select your Trust", value: "Select your Trust" }];

    if (this.props.screenProps.hostApp === "CFR") {
      trustDropdownOptions.push({ label: "General subscriber", value: "General subscriber" });
    }

    for (let trust of trusts) {
      trustDropdownOptions.push({ label: DataController.getTrustDisplayName(trust), value: trust });
    }

    let shouldShowTemporaryLoginPasswordInput = false;
    if (
      this.props.screenProps.hostApp === "CFR" &&
      this.state.trustDropdownValue !== "General subscriber" &&
      this.state.trustDropdownValue !== "Select your Trust"
    ) {
      shouldShowTemporaryLoginPasswordInput = true;
    }

    let temporaryLoginButtonIsEnabled = false;
    if (this.props.screenProps.hostApp === "CFR") {
      if (this.state.trustDropdownValue !== "Select your Trust") {
        if (this.state.trustDropdownValue === "General subscriber") {
          temporaryLoginButtonIsEnabled = true;
        } else {
          if (this.state.tempPasswordText.length > 0) {
            temporaryLoginButtonIsEnabled = true;
          }
        }
      }
    } else {
      if (this.state.trustDropdownValue !== "Select your Trust") {
        temporaryLoginButtonIsEnabled = true;
      }
    }

    return (
      <IonPage>
        <IonHeader>
          <IonToolbar mode="ios" className="navBar">
            <IonButtons slot="end">
              {this.props.addingAccount ? (
                <IonButton onClick={this.props.screenProps.showAddAccountLoginModal.bind(this, false)}>
                  <img className="icon-44" src={iconClose} />
                </IonButton>
              ) : null}
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent fullscreen>
          <AppHeader app={this.props.screenProps.hostApp} trust={this.props.screenProps.user?.trust} />

          <div className="globalContentMargin">
            {this.state.showWebBanner ? (
              <div className="webAppBanner">
                <div className="webAppBannerTitle">Updated</div>
                <div>
                  The iCPG web app has been updated with a new look and additional functionality. This update will be
                  launched on Apple and Android devices in due course. Please get in touch at{" "}
                  <a
                    className="webAppBannerLink"
                    href="#"
                    onClick={this.props.screenProps.sendSupportEmail.bind(this, "Feedback")}
                  >
                    apps@class.co.uk
                  </a>{" "}
                  about any feedback you have.
                </div>
                <IonButton
                  mode="ios"
                  className="webAppBannerButton clearButton"
                  onClick={this.dismissWebBanner.bind(this)}
                >
                  Dismiss
                </IonButton>
              </div>
            ) : null}

            {this.state.showTemporaryLogin ? (
              <div className="loginContent">
                <AppCard title="Temporary log in" darkMode={this.props.screenProps.darkMode}>
                  Select your Trust from the dropdown below and tap ‘Temporary log in’. When the servers are available
                  again, you will be asked to log back into your account.
                  <Dropdown
                    className="temporaryLoginDropdown"
                    selectOptions={trustDropdownOptions}
                    value={this.state.trustDropdownValue}
                    dropdownValueChanged={(value) => this.setState({ trustDropdownValue: value })}
                  />
                  {shouldShowTemporaryLoginPasswordInput ? (
                    <>
                      <div style={{ marginTop: 10, marginBottom: 10 }}>Please enter your trust provided password</div>
                      <div className={`loginInputDiv ${this.state.tempPasswordActive ? "loginInputDivActive" : ""}`}>
                        <IonInput
                          value={this.state.tempPasswordText}
                          type="text"
                          className="loginInputField"
                          placeholder="Password"
                          onIonInput={(event) => this.tempPasswordChanged(event)}
                          onIonFocus={() => {
                            this.setState({
                              tempPasswordActive: true,
                            });
                          }}
                          onIonBlur={() => this.setState({ tempPasswordActive: false })}
                          onKeyDown={(event) => {
                            if (event.key === "enter") {
                              this.loginTemporarily();
                            }
                          }}
                        />
                      </div>
                      {this.state.showTemporaryLoginIncorrectPassword ? (
                        <div className="errorText">Incorrect Password</div>
                      ) : null}
                    </>
                  ) : null}
                  <div className="loginTemporaryButtonContainer">
                    <IonButton
                      mode="ios"
                      className="filledButton"
                      strong={true}
                      disabled={!temporaryLoginButtonIsEnabled}
                      onClick={() => {
                        this.loginTemporarily();
                      }}
                    >
                      <IonLabel mode="ios">Temporary log in</IonLabel>
                    </IonButton>
                  </div>
                  <div className="loginTopMargin">You can check the current server status by viewing this link:</div>
                  <a href="https://www.classprofessional.co.uk/terms-of-use/apps-status" target="_blank">
                    www.classprofessional.co.uk/terms-of-use/apps-status
                  </a>
                  <IonButton
                    onClick={this.showTemporaryLogin.bind(this, false)}
                    className="loginTopMargin outlineButton"
                  >
                    Try log in again
                  </IonButton>
                </AppCard>
              </div>
            ) : (
              <div className="loginContent">
                <AppCard
                  title={this.state.loginType === "login" ? "Log in" : "Sign up"}
                  darkMode={this.props.screenProps.darkMode}
                >
                  {this.state.loginType === "login" && this.props.addingAccount ? (
                    <div className="loginSharedDeviceWarning">
                      Please note: It is not recommended to add another account on a shared device - you should log out
                      instead. If you would like to proceed, you will need to log out of all your accounts at the end of
                      your shift.
                    </div>
                  ) : null}

                  <form>
                    {this.state.loginType === "register" ? (
                      <div>
                        <div className={`loginInputLabel ${this.state.firstNameActive ? "loginInputLabelActive" : ""}`}>
                          {"First name"}
                        </div>
                        <div className={`loginInputDiv ${this.state.firstNameActive ? "loginInputDivActive" : ""}`}>
                          <IonInput
                            value={this.state.firstNameText}
                            type="text"
                            className="loginInputField"
                            placeholder="First name"
                            onIonInput={(event) => this.firstNameChanged(event)}
                            onIonFocus={() => {
                              this.setState({
                                firstNameActive: true,
                              });
                              this.resetErrorMessages();
                            }}
                            onIonBlur={() => this.setState({ firstNameActive: false })}
                            onKeyDown={(event) => this.enterButtonClicked(event)}
                          />
                        </div>

                        <div className={`loginInputLabel ${this.state.lastNameActive ? "loginInputLabelActive" : ""}`}>
                          {"Last name"}
                        </div>
                        <div className={`loginInputDiv ${this.state.lastNameActive ? "loginInputDivActive" : ""}`}>
                          <IonInput
                            value={this.state.lastNameText}
                            type="text"
                            className="loginInputField"
                            placeholder="Last name"
                            onIonInput={(event) => this.lastNameChanged(event)}
                            onIonFocus={() => {
                              this.setState({
                                lastNameActive: true,
                              });
                              this.resetErrorMessages();
                            }}
                            onIonBlur={() => this.setState({ lastNameActive: false })}
                            onKeyDown={(event) => this.enterButtonClicked(event)}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div className={`loginInputLabel ${this.state.emailActive ? "loginInputLabelActive" : ""}`}>
                      {"Email"}
                    </div>
                    <div className={`loginInputDiv ${this.state.emailActive ? "loginInputDivActive" : ""}`}>
                      <IonInput
                        value={this.state.emailText}
                        type="email"
                        className="loginInputField"
                        placeholder="Email"
                        autocomplete="email"
                        onIonInput={(event) => this.emailChanged(event)}
                        onIonFocus={() => {
                          this.setState({
                            emailActive: true,
                          });
                          this.resetErrorMessages();
                        }}
                        onIonBlur={() => this.setState({ emailActive: false })}
                        onKeyDown={(event) => this.enterButtonClicked(event)}
                      />
                    </div>

                    {this.state.loginType === "register" ? (
                      <div>
                        <div
                          className={`loginInputLabel ${
                            this.state.emailConfirmationActive ? "loginInputLabelActive" : ""
                          }`}
                        >
                          {"Email confirmation"}
                        </div>
                        <div
                          className={`loginInputDiv ${this.state.emailConfirmationActive ? "loginInputDivActive" : ""}`}
                        >
                          <IonInput
                            value={this.state.emailConfirmationText}
                            type="email"
                            className="loginInputField"
                            placeholder="Email confirmation"
                            onIonInput={(event) => this.emailConfirmationChanged(event)}
                            onIonFocus={() => {
                              this.setState({
                                emailConfirmationActive: true,
                              });
                              this.resetErrorMessages();
                            }}
                            onIonBlur={() => this.setState({ emailConfirmationActive: false })}
                            onKeyDown={(event) => this.enterButtonClicked(event)}
                          />
                        </div>
                      </div>
                    ) : null}

                    <div>
                      <div className={`loginInputLabel ${this.state.passwordActive ? "loginInputLabelActive" : ""}`}>
                        {"Password"}
                      </div>
                      <div className={`loginInputDiv ${this.state.passwordActive ? "loginInputDivActive" : ""}`}>
                        <IonInput
                          id="passwordInput"
                          value={this.state.passwordText}
                          type={this.state.passwordHidden ? "password" : "text"}
                          className="loginInputField"
                          placeholder="••••••••••"
                          autocomplete="current-password"
                          onIonInput={(event) => this.passwordChanged(event)}
                          clearOnEdit={false}
                          ref={this.passwordField}
                          onIonFocus={() => {
                            this.setState({
                              passwordActive: true,
                            });
                            this.resetErrorMessages();
                          }}
                          onIonBlur={() => this.setState({ passwordActive: false })}
                          onKeyDown={(event) => this.enterButtonClicked(event)}
                        ></IonInput>
                        <IonButton
                          className="passButton"
                          mode="ios"
                          tabIndex={-1} // -1 so that it can't be tabbed to (was inbetween login and password)
                          onClick={() => {
                            this.passwordField.current?.setFocus();
                            this.setState({ passwordHidden: !this.state.passwordHidden });
                          }}
                        >
                          <img
                            alt=""
                            src={this.state.passwordHidden ? iconHidePassword : iconShowPassword}
                            className="passIcon"
                          />
                        </IonButton>
                      </div>
                    </div>

                    {this.state.loginType === "register" ? (
                      <div>
                        <div
                          className={`loginInputLabel ${
                            this.state.passwordConfirmationActive ? "loginInputLabelActive" : ""
                          }`}
                        >
                          {"Password confirmation"}
                        </div>
                        <div
                          className={`loginInputDiv ${
                            this.state.passwordConfirmationActive ? "loginInputDivActive" : ""
                          }`}
                        >
                          <IonInput
                            id="passwordInput"
                            value={this.state.passwordConfirmationText}
                            type={this.state.passwordHidden ? "password" : "text"}
                            className="loginInputField"
                            placeholder="••••••••••"
                            onIonInput={(event) => this.passwordConfirmationChanged(event)}
                            clearOnEdit={false}
                            ref={this.passwordConfirmationField}
                            onIonFocus={() => {
                              this.setState({
                                passwordConfirmationActive: true,
                              });
                              this.resetErrorMessages();
                            }}
                            onIonBlur={() => this.setState({ passwordConfirmationActive: false })}
                            onKeyDown={(event) => this.enterButtonClicked(event)}
                          ></IonInput>
                          <IonButton
                            className="passButton"
                            mode="ios"
                            tabIndex={-1} // -1 so that it can't be tabbed to (was inbetween login and password)
                            onClick={() => {
                              this.passwordConfirmationField.current?.setFocus();
                              this.setState({ passwordHidden: !this.state.passwordHidden });
                            }}
                          >
                            <img
                              alt=""
                              src={this.state.passwordHidden ? iconHidePassword : iconShowPassword}
                              className="passIcon"
                            />
                          </IonButton>
                        </div>
                      </div>
                    ) : null}
                  </form>
                  <div className="loginSubmitButtonContainer">
                    <IonButton
                      mode="ios"
                      className="accountLoginButton filledButton"
                      strong={true}
                      onClick={() => {
                        this.resetErrorMessages();
                        this.submit();
                      }}
                      disabled={!this.state.submitButtonEnabled}
                    >
                      <IonLabel mode="ios">{this.state.loginType === "login" ? "Log in" : "Sign up"}</IonLabel>
                    </IonButton>
                  </div>

                  {this.props.addingAccount !== true && this.props.screenProps.rememberedUsers.length > 0 ? (
                    <div>
                      <div className="orSwitchAccountContainer">
                        <span>or</span>
                      </div>

                      <div className="loginSubmitButtonContainer">
                        <IonButton
                          mode="ios"
                          className="accountLoginButton outlineButton"
                          strong={true}
                          onClick={this.props.screenProps.showAccountSwitcher.bind(this, true)}
                        >
                          <IonLabel mode="ios">Switch account</IonLabel>
                        </IonButton>
                      </div>
                    </div>
                  ) : null}

                  {this.state.ssoTrusts.length > 0 ? (
                    <div>
                      <div className="loginSubmitButtonContainer">
                        <IonButton
                          mode="ios"
                          className="accountLoginButton greyFilledButton ssoLoginButton"
                          strong={true}
                          onClick={() => this.setState({ showSSOModal: true })}
                        >
                          <IonLabel mode="ios">Log in with Trust portal</IonLabel>
                        </IonButton>
                      </div>
                    </div>
                  ) : null}

                  {this.state.loginError ? (
                    <div className="loginError">
                      <img alt="" src={iconError} className="errorIcon" />
                      <span className="errorText">{this.state.errorMessage}</span>
                    </div>
                  ) : null}

                  {this.state.showTemporaryLoginErrorMessage ? (
                    <div className="temporaryLoginContainer">
                      <img alt="" src={iconError} className="errorIcon" />
                      <div className="errorText">
                        {this.props.screenProps.hostApp === "iCPG" ? (
                          <div>
                            Login failed. There appears to be an issue with the servers. Tap Use temporary log in below
                            to access the app using a temporary login. It is recommended that you try logging in again
                            before using your temporary login.
                          </div>
                        ) : (
                          <div>
                            Login failed. There appears to be an issue with the servers. Tap Use temporary log in below
                            to access the app using your temporary login provided by your Trust. It is recommended that
                            you try logging in again before using your temporary login.
                          </div>
                        )}
                      </div>
                      <div className="loginTemporaryButtonContainer">
                        <IonButton
                          mode="ios"
                          id="loginTemporaryButton"
                          className="filledButton"
                          strong={true}
                          onClick={() => {
                            if (this.props.screenProps.hostApp === "iCPG") {
                              this.loginTemporarily();
                            } else {
                              this.showTemporaryLogin(true);
                            }
                          }}
                        >
                          <IonLabel mode="ios">Use temporary log in</IonLabel>
                        </IonButton>
                      </div>
                    </div>
                  ) : null}

                  {this.props.screenProps.hostApp !== "Plus" ? (
                    <div>
                      <IonButton
                        mode="ios"
                        className="clearButton loginSwitchTypeButton"
                        onClick={this.switchLoginType.bind(this)}
                      >
                        {this.state.loginType === "login" ? (
                          <span>Don't have an account? Sign up here</span>
                        ) : (
                          <span>Already have an account? Log in here</span>
                        )}
                      </IonButton>
                    </div>
                  ) : null}
                </AppCard>

                <AppCard title="Password issues" darkMode={this.props.screenProps.darkMode}>
                  <div className="loginPageCardTitle">Why has my password expired?</div>
                  <div className="loginPageCardText">
                    You may be required to update your password for security reasons.
                  </div>
                  <div className="loginPageCardTitle">How do I reset my password?</div>
                  <div className="loginPageCardText">
                    To reset your password, go to{" "}
                    <a href="https://www.classprofessional.co.uk/app-reset" target="_blank">
                      www.classprofessional.co.uk/app-reset
                    </a>{" "}
                    and enter your email address. This will send you a link to reset your password. If you have any
                    problems at all, check our{" "}
                    <a href="https://www.classprofessional.co.uk/faqs" target="_blank">
                      FAQs
                    </a>
                    , get in touch with us at{" "}
                    <span
                      className="loginLink"
                      onClick={this.props.screenProps.sendSupportEmail.bind(this, "Subscription Support")}
                    >
                      apps@class.co.uk
                    </span>{" "}
                    or call us on <a href="tel://01278427800">01278 427800</a>.
                  </div>
                </AppCard>

                {this.props.screenProps.hostApp === "iCPG" ? (
                  <AppCard title="Disclaimer" darkMode={this.props.screenProps.darkMode}>
                    <div className="loginPageCardText">
                      iCPG is for information purposes only and is neither a diagnostic nor a treatment tool. The UK
                      Ambulance Services Clinical Practice Guidelines are published by Class Professional Publishing, an
                      imprint of Class Publishing Ltd, and approved by the Association of Ambulance Chief Executives
                      (AACE) and the Joint Royal Colleges Ambulance Liaison Committee (JRCALC). CPP, AACE and JRCALC
                      have made every effort to ensure that the information contained within iCPG is reflective of the
                      print guidelines and accurate but make no guarantee whatsoever, and accept no legal liability of
                      whatever nature arising from or connected to, the accuracy, reliability, currency or completeness
                      of the content of the iCPG and the print guidelines. iCPG and the print guidelines are in no way a
                      substitute for sound clinical judgement and skill. Healthcare professionals must practise within
                      their level of training and competence.
                    </div>
                  </AppCard>
                ) : null}

                {this.props.screenProps.hostApp === "CFR" ? (
                  <AppCard title="Background: The Responder Plus app" darkMode={this.props.screenProps.darkMode}>
                    <div className="loginPageCardText">
                      <p>
                        The Responder Plus app is for information purposes only and is neither a diagnostic nor a
                        treatment tool. By accessing this app, you agree to these terms and conditions of use.
                      </p>
                      <p>
                        Responders must practice according to the local policies of your organisation. Do not carry out
                        procedures unless they are within your scope of practice.
                      </p>
                      <p>
                        The Responder Plus app can be used by individuals by purchasing a subscription. The app can also
                        be provided by ambulance trusts for their staff, in which case the subscription will be arranged
                        via Class Publishing directly.
                      </p>
                    </div>
                  </AppCard>
                ) : null}

                {this.props.screenProps.hostApp === "CFR" ? (
                  <AppCard title="Privacy" darkMode={this.props.screenProps.darkMode}>
                    <div className="loginPageCardText">
                      <p>Class Publishing Ltd will hold any information that you provide to us confidentially.</p>
                      <p>
                        When you create an account on the Responder Plus app we ask for your name and email address so
                        we know who you are. We use a trusted third-party supplier to hold this user data securely.
                        Class Professional Publishing collects data on who has logged in to the Responder Plus app.
                      </p>
                      <p>
                        Should the app subscription be provided to you by your ambulance trust or other organisation,
                        this data may also be made available to them and any parties they choose to share this
                        information with.
                      </p>
                    </div>
                  </AppCard>
                ) : null}

                <AppCard title="Your Rights" darkMode={this.props.screenProps.darkMode}>
                  <div className="loginPageCardText">
                    Under the General Data Protection Regulation (GDPR) and other information rights laws, you have
                    rights as an individual which we will always respect. You can read more about your rights here:{" "}
                    <a href="https://ico.org.uk" target="_blank">
                      https://ico.org.uk
                    </a>
                  </div>
                </AppCard>

                {this.props.screenProps.hostApp === "CFR" ? (
                  <AppCard title="Copyright" darkMode={this.props.screenProps.darkMode}>
                    <div className="loginPageCardText">
                      <p>The JRCALC Guidelines are copyright of the Association of Ambulance Chief Executives.</p>
                      <p>First Responder Care Essentials is copyright of Richard Pilbery and Kris Lethbridge.</p>
                    </div>
                  </AppCard>
                ) : null}

                <AppCard title="FAQs" darkMode={this.props.screenProps.darkMode}>
                  <div className="loginPageCardText">
                    Read the app FAQ here:{" "}
                    <a href="https://www.classprofessional.co.uk/faqs" target="_blank">
                      www.classprofessional.co.uk/faqs
                    </a>
                  </div>
                </AppCard>

                <AppCard title="Contacting us" darkMode={this.props.screenProps.darkMode}>
                  <div className="loginPageCardText">
                    Class Professional Publishing will be pleased to answer any of your questions you have about the
                    JRCALC apps. You can call us on <a href="tel://01278427800">01278 427800</a> or email us at{" "}
                    <span
                      className="loginLink"
                      onClick={this.props.screenProps.sendSupportEmail.bind(this, "Feedback")}
                    >
                      apps@class.co.uk
                    </span>
                  </div>
                  {this.state.showOlark ? (
                    <>
                      You can also start a chat with us, our customer support team are ready to answer any questions you
                      have from 9am to 5pm.
                      <IonButton
                        style={{ marginTop: "10px" }}
                        mode="ios"
                        className="outlineButton"
                        strong={true}
                        onClick={() => {
                          this.props.screenProps.showOlark(true);
                        }}
                      >
                        Start Chat
                      </IonButton>
                    </>
                  ) : null}
                </AppCard>
              </div>
            )}

            <IonModal
              id="ssoModal"
              mode="ios"
              isOpen={this.state.showSSOModal}
              onDidDismiss={() => this.setState({ showSSOModal: false })}
            >
              <IonHeader>
                <IonToolbar mode="ios" color="#F7961E" className="modalHeader navBar">
                  <IonButtons slot="end">
                    <IonButton className="headerButtons" onClick={() => this.setState({ showSSOModal: false })}>
                      <img className="icon-44" src={buttonClose} />
                    </IonButton>
                  </IonButtons>
                </IonToolbar>
              </IonHeader>
              <IonContent className="ssoModalContent">
                <div className="globalContentMargin">
                  <div className="ssoModalTitle">Select your Trust</div>
                  <div className="ssoModalDescription">
                    If your Trust is not listed below, please go back to the JRCALC Plus login
                  </div>
                  {this.state.ssoTrusts.map((ssoTrust, i: number) => (
                    <div className="ssoModalTrustRow" onClick={() => this.loginSSO(ssoTrust)}>
                      <div className="ssoModalTrustTitle">{DataController.getTrustDisplayName(ssoTrust)}</div>
                      <img alt="" src={disclosureRight} className="icon-44" />
                    </div>
                  ))}
                </div>
              </IonContent>
            </IonModal>

            <IonLoading
              cssClass="logginginDialogue"
              isOpen={this.state.isLoggingIn}
              message={this.state.loginType === "login" ? "Signing in" : "Signing up"}
            />
          </div>
        </IonContent>
      </IonPage>
    );
  }
}

export default LoginPage;
